import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { getPlanDisplayName } from "../../utils/pricingDisplay.js";
import "./billingPaymentDetail.css";

function fmt(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function money(paise, currency = "INR") {
  if (!Number.isFinite(Number(paise)) || Number(paise) <= 0) return "-";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(paise) / 100);
  } catch {
    return `${(Number(paise) / 100).toFixed(2)} ${currency}`;
  }
}

const getStatusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "succeeded" || s === "paid") return "bp-badge--success";
  if (s === "failed") return "bp-badge--failed";
  return "bp-badge--pending";
};

const FIELDS = [
  { key: "id",                label: "Record ID",          mono: true },
  { key: "planName",          label: "Plan",
    render: r => {
      const planName = getPlanDisplayName(r.planId, r.planName) || "-";
      if (r.purchaseMode === "agents" || r.purchaseKind === "agent_addon") {
        return `${planName} (Additional Agents Add-on)`;
      }
      return planName;
    }
  },
  { key: "agentCount",        label: "Seats Purchased",
    render: r => (r.purchaseMode === "agents" || r.purchaseKind === "agent_addon") && r.agentCount
      ? `${r.agentCount} ${r.agentCount === 1 ? "Agent" : "Agents"}`
      : null
  },
  { key: "status",            label: "Status",             badge: true,
    cls: r => getStatusBadgeClass(r.status) },
  { key: "amount",            label: "Subtotal",
    render: r => money(r.amount, r.currency) },
  { key: "taxAmount",         label: "GST (18%)",
    render: r => money(r.taxAmount, r.currency) },
  { key: "totalAmount",       label: "Total Paid",
    render: r => money(r.totalAmount || r.amount, r.currency) },
  { key: "paymentMode",       label: "Payment Mode" },
  { key: "razorpayPaymentId", label: "Razorpay Payment ID", mono: true },
  { key: "razorpayOrderId",   label: "Razorpay Order ID",   mono: true },
  { key: "notes",             label: "Notes" },
  { key: "failureReason",     label: "Failure Reason",
    render: r => r.failureReason || null },
  { key: "paidAt",            label: "Paid At",
    render: r => {
      const s = String(r.status || "").toLowerCase();
      if (s === "succeeded" || s === "paid") return fmt(r.paidAt || r.updatedAt);
      return null;
    }
  },
  { key: "createdAt",         label: "Initiated At",       render: r => fmt(r.createdAt) },
  { key: "updatedAt",         label: "Last Updated",
    render: r => {
      const s = String(r.status || "").toLowerCase();
      if (s === "failed" || s === "refunded" || s === "chargeback") return fmt(r.updatedAt);
      return null;
    }
  },
];

function FieldRow({ field, record }) {
  let value;
  if (field.render) {
    value = field.render(record);
  } else {
    const raw = record[field.key];
    value = raw != null && raw !== "" ? String(raw) : null;
  }
  if (!value || value === "-") return null;

  return (
    <div className="bp-field">
      <span className="bp-field-label">{field.label}</span>
      {field.badge ? (
        <span className={`bp-badge ${field.cls(record)}`}>{value}</span>
      ) : (
        <span className={`bp-field-value${field.mono ? " bp-mono" : ""}`}>{value}</span>
      )}
    </div>
  );
}

export default function BillingPaymentDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const record   = location.state?.record || null;
  const savedScrollY = location.state?.scrollY;

  // Navigate back and restore exact scroll position — no animation, no top-to-bottom sweep
  const goBack = () => navigate("/billing", { state: { restoreScrollY: savedScrollY ?? 0 } });

  return (
    <div className="bp-page">
      {/* Back — icon only, at the top */}
      <div className="bp-breadcrumb">
        <button className="gc-icon-btn" onClick={goBack} data-tip="Back to Billing" aria-label="Back to Billing">
          <ArrowLeft size={16} />
        </button>
        <span className="bp-breadcrumb-current">Payment Detail</span>
      </div>

      {!record ? (
        <div className="bp-notfound">
          <p>Record not found.</p>
          <button className="gc-icon-btn" onClick={goBack} data-tip="Back to Billing" aria-label="Back to Billing">
            <ArrowLeft size={16} />
          </button>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="bp-header">
            <div>
              <p className="bp-sub">{getPlanDisplayName(record.planId, record.planName) || "Payment record"}</p>
            </div>
            <div className="bp-header-actions">
              <span className={`bp-badge ${getStatusBadgeClass(record.status)}`}>
                {record.status || "-"}
              </span>
              <button
                className="bp-close-btn"
                onClick={goBack}
                title="Close and return to Billing"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Fields */}
          <div className="bp-card">
            <div className="bp-fields-grid">
              {FIELDS.map(f => <FieldRow key={f.key} field={f} record={record} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
